import * as React from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Grid,
  Paper,
  Typography,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DescriptionIcon from "@mui/icons-material/Description";
import FolderIcon from "@mui/icons-material/Folder";
import GroupsIcon from "@mui/icons-material/Groups";
import ImageIcon from "@mui/icons-material/Image";
import InsightsIcon from "@mui/icons-material/Insights";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import QuizIcon from "@mui/icons-material/Quiz";
import { Link } from "react-router-dom";
import {
  useAnswersQuery,
  useDocumentsQuery,
  useSliderCollectionsQuery,
  useUsersQuery,
} from "../../queries/useToeicQueries";

const modules = [
  {
    title: "Tests",
    href: "/management/test",
    createHref: "/test/add",
    icon: QuizIcon,
    statKey: "tests",
    description: "Manage answer sheets, audio files, answer keys, and test content.",
    color: "#2196f3",
    bg: "#edf7ff",
    border: "#b9dcff",
    action: "Manage tests",
  },
  {
    title: "Documents",
    href: "/management/document",
    createHref: "/document/add",
    icon: FolderIcon,
    statKey: "documents",
    description: "Maintain downloadable TOEIC materials and document metadata.",
    color: "#35c759",
    bg: "#e8f9ed",
    border: "#a9eabc",
    action: "Manage documents",
  },
  {
    title: "Slider",
    href: "/management/collection",
    createHref: "/slider/add",
    icon: ImageIcon,
    statKey: "collections",
    description: "Review homepage slider collections and activate weekly content.",
    color: "#d81b60",
    bg: "#ffe8f0",
    border: "#ffb8cf",
    action: "Manage slider",
  },
  {
    title: "Users",
    href: "/management/user",
    icon: GroupsIcon,
    statKey: "users",
    description: "Inspect accounts, roles, access state, and admin permissions.",
    color: "#6d4aff",
    bg: "#f0ecff",
    border: "#c6b8ff",
    action: "Manage users",
  },
];

function Management() {
  const { data: answers = [] } = useAnswersQuery("");
  const { data: documents = [] } = useDocumentsQuery("");
  const { data: collections = [] } = useSliderCollectionsQuery("");
  const { data: users = [] } = useUsersQuery("");
  const stats = {
    tests: answers.length,
    documents: documents.length,
    collections: collections.length,
    users: users.length,
  };

  const theme = createTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        width: "100%",
        mt: 4,
        mb: 6,
      }}
    >
      <Box component="header" sx={{ maxWidth: 900 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr auto",
            gap: 3,
            alignItems: "center",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box>
              <Chip
                size="small"
                icon={<ManageAccountsIcon />}
                label="Admin workspace"
                sx={{
                  mb: 1,
                  bgcolor: "#e8f9ed",
                  color: "#1f9b45",
                  "& .MuiChip-icon": { color: "#1f9b45" },
                }}
              />
              <Typography
                component="h1"
                sx={{
                  fontWeight: 800,
                  color: "var(--toeic-ink-deep)",
                  fontSize: { xs: 36, md: 56 },
                  letterSpacing: 0,
                  lineHeight: { xs: 1.05, md: 1 },
                }}
              >
                <Box component="span" sx={{ position: "relative", display: "inline-block" }}>
                  <Box
                    component="span"
                    aria-hidden="true"
                    sx={{
                      position: "absolute",
                      left: "-0.12em",
                      right: "-0.12em",
                      bottom: "0.04em",
                      height: "0.44em",
                      bgcolor: "rgba(70, 168, 232, 0.24)",
                      borderRadius: "0.45em 0.6em 0.4em 0.55em",
                      transform: "rotate(-1deg)",
                    }}
                  />
                  <Box component="span" sx={{ position: "relative" }}>
                    Website Management
                  </Box>
                </Box>
                .
              </Typography>
              <Typography sx={{ mt: 1, color: "var(--toeic-ink-soft)", maxWidth: 620, fontWeight: 700, lineHeight: 1.7 }}>
                Control tests, learning documents, homepage slider collections,
                and user accounts from one clean dashboard.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: isMobile ? "stretch" : "flex-end",
              flexWrap: "wrap",
            }}
          >
            <Button
              component={Link}
              to="/test/add"
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                bgcolor: "#35c759",
                color: "#ffffff",
                fontWeight: 700,
                "&:hover": { bgcolor: "#2fb44f" },
              }}
            >
              New test
            </Button>
            <Button
              component={Link}
              to="/document/add"
              variant="outlined"
              startIcon={<DescriptionIcon />}
              sx={{ borderColor: "#2196f3", color: "#2196f3", fontWeight: 700 }}
            >
              New document
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 4, mb: 4, display: "grid", gap: 4 }}>
        <Box aria-hidden sx={{ borderTop: "1.5px dotted var(--toeic-line)" }} />
        <Box
          className="toeic-reveal"
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: "18px",
            border: "1px solid rgba(215, 234, 245, 0.82)",
            bgcolor: "var(--toeic-paper-grid)",
            boxShadow: "var(--toeic-shadow-card)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              color: "var(--toeic-ink)",
              fontSize: 14,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: ".12em",
            }}
          >
            Admin Modules
          </Typography>
          <Typography sx={{ color: "var(--toeic-ink-soft)", fontSize: 12, fontWeight: 700 }}>
            {modules.length} modules - quick actions
          </Typography>
        </Box>
        <Box aria-hidden sx={{ borderTop: "1.5px dotted var(--toeic-line)" }} />
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {modules.map((module) => {
          const Icon = module.icon;

          return (
            <Grid item xs={12} sm={6} md={3} key={module.title}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  height: "100%",
                  borderRadius: "14px",
                  border: 0,
                  bgcolor: module.bg,
                  boxShadow: "var(--toeic-shadow-card)",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "14px",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "#ffffff",
                    color: module.color,
                    flex: "0 0 auto",
                  }}
                >
                  <Icon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: module.color, fontWeight: 700, fontSize: 24 }}>
                    {stats[module.statKey]}
                  </Typography>
                  <Typography sx={{ color: "var(--toeic-ink-deep)", fontWeight: 800 }}>
                    {module.title}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
          gap: 2.5,
        }}
      >
        {modules.map((module) => {
          const Icon = module.icon;

          return (
            <Card
              key={module.title}
              elevation={0}
              sx={{
                borderRadius: "14px",
                border: 0,
                bgcolor: "var(--toeic-surface)",
                boxShadow: "var(--toeic-shadow-card)",
                overflow: "hidden",
                transition: "border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  borderColor: module.border,
                  transform: "translateY(-2px)",
                  boxShadow: "var(--toeic-shadow-hover)",
                },
              }}
            >
              <CardActionArea
                component={Link}
                to={module.href}
                sx={{
                  p: 2.5,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 54,
                      height: 54,
                      borderRadius: "14px",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: module.bg,
                      color: module.color,
                      flex: "0 0 auto",
                    }}
                  >
                    <Icon sx={{ fontSize: 30 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: "var(--toeic-ink-deep)", fontWeight: 700, fontSize: 20 }}>
                      {module.title}
                    </Typography>
                    <Typography sx={{ color: module.color, fontWeight: 700, fontSize: 13 }}>
                      {stats[module.statKey]} records
                    </Typography>
                  </Box>
                </Box>

                <Typography sx={{ color: "var(--toeic-ink-soft)", lineHeight: 1.6, fontWeight: 700 }}>
                  {module.description}
                </Typography>

                <Box
                  sx={{
                    mt: "auto",
                    pt: 1.5,
                    borderTop: "1.5px dotted var(--toeic-line)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Chip
                    size="small"
                    icon={<InsightsIcon />}
                    label={module.action}
                    sx={{
                      bgcolor: module.bg,
                      color: module.color,
                      "& .MuiChip-icon": { color: module.color },
                    }}
                  />
                  <ArrowForwardIcon sx={{ color: module.color }} />
                </Box>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
}

export default Management;
